<template>
  <aside
    class="grid grid-cols-1 gap-1 grid-rows-[100px_1fr_auto] h-screen w-full border-r border-surface-200 bg-white relative"
  >
    <Button
      icon="pi pi-chevron-left"
      rounded
      size="small"
      variant="text"
      severity="secondary"
      class="!absolute right-2 top-2"
      @click="emit('toggle')"
      v-tooltip.right="t('app.hide_menu')"
    />
    <div class="flex gap-2 items-center pl-3 border-surface-200">
      <img
        v-if="authStore.entity.config.getStatic('theme') === 'epro'"
        src="../../public/epro/fav_blue.png"
        alt="CoralCare"
        class="h-9"
      />
      <img v-else src="../../public/favicon.ico" alt="Mimbly" class="h-7" />
      <p class="font-heading font-bold text-primary text-xl leading-4 align-self-baseline">
        {{ t("brand.site") }}
      </p>
    </div>

    <nav class="flex-1 flex gap-2 flex-col overflow-y-auto px-1.5 pl-2 py-1 no-scrollbar">
      <section>
        <SidebarLink to="/mimbox" icon="pi pi-box" :label="t('brand.mimboxes')" />

        <SidebarLink
          v-if="authStore.is.staff()"
          :label="t('common.fields.entities')"
          icon="pi pi-building"
          :to="{
            name: 'entities',
          }"
        />
        <SidebarLink
          v-else-if="authStore?.entity?.id"
          :to="{ name: 'entity-home', params: { id: authStore.entity.id } }"
          icon="pi pi-building"
          :label="`${authStore.entity.name || authStore.entity.name || t('common.messages.unknown')}`"
        />
      </section>

      <section>
        <p class="gap-3 px-2 py-2 text-xs text-gray-500">{{ t("nav.reports") }}</p>
        <SidebarLink
          :label="t('nav.mb2_error')"
          icon="pi pi-chart-bar"
          to="/report/error"
          v-if="authStore.is.staff()"
        />
        <SidebarLink
          :label="t('nav.downloadable_reports')"
          icon="pi pi-download"
          :to="{ name: 'downloadable-reports' }"
        />
      </section>
      <section v-if="authStore.is.staff()">
        <p class="gap-3 px-2 py-2 text-xs text-gray-500">{{ t("nav.staff") }}</p>
        <SidebarLink
          :label="t('nav.manage_staff_users')"
          icon="pi pi-users"
          :to="{
            name: 'staffUsers',
          }"
        />
        <SidebarLink
          :label="t('nav.manage_files')"
          icon="pi pi-file"
          :to="{
            name: 'staffFiles',
          }"
        />
        <SidebarLink :label="t('nav.fleet_manager')" icon="pi pi-box" to="/fleet-manager" />
        <SidebarLink label="C1" icon="pi pi-wrench" :to="{ name: 'C1' }" />
        <SidebarLink
          :label="t('nav.qr_code_manager')"
          icon="pi pi-qrcode"
          to="/admin/qr-code-manager"
        />
        <SidebarLink
          :label="t('nav.passcode_manager')"
          icon="pi pi-key"
          to="/admin/passcode-manager"
        />
        <SidebarLink
          :label="t('nav.translations')"
          icon="pi pi-language"
          to="/admin/translations"
        />
      </section>
      <section>
        <p class="gap-3 px-2 py-2 text-xs text-gray-500">{{ t("nav.resources") }}</p>
        <SidebarLink
          to="/information"
          icon="pi pi-info-circle"
          :label="t('common.fields.guides')"
        />
      </section>
    </nav>

    <div class="border-t border-surface-200 relative group">
      <Tag
        class="absolute -top-3 right-2 !cursor-default outline-1 outline-transparent group-hover:outline-inherit transition-all delay-300"
        v-if="authStore.user?.role"
        :severity="`user-role-${authStore.user.role}`"
        :value="capitalize(authStore.user.role.replace('-', ''))"
        :pt="{ root: '!text-[7.5pt]' }"
        v-tooltip.right="t('nav.user_role')"
      />
      <div
        class="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-surface-100 transition-colors"
        data-cy="user-menu"
        @click="toggleMenu"
      >
        <div
          class="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold shrink-0"
        >
          {{ userInitial }}
        </div>
        <div class="min-w-0 flex-1">
          <p class="text-sm font-semibold text-gray-700 truncate">{{ userName }}</p>
          <p class="text-xs text-gray-500 truncate">{{ userEmail }}</p>
        </div>
        <i class="pi pi-ellipsis-v text-gray-400 text-sm" />
      </div>
      <Menu
        ref="menu"
        :model="userMenuItems"
        popup
        :pt="{
          itemLabel: 'truncate w-40 text-sm !leading-4',
        }"
      />
    </div>
  </aside>
</template>

<script setup lang="ts">
import { capitalize, computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { Menu, Button, Tag } from "primevue";
import { useAuthStore } from "@/stores/auth";
import SidebarLink from "./AppSidebar/SidebarLink.vue";
import { useRouter } from "vue-router";
const emit = defineEmits<{ navigate: []; toggle: [] }>();

const { t } = useI18n();
const authStore = useAuthStore();
const menu = ref();
const router = useRouter();

const userName = computed(() => {
  if (authStore.user?.displayName) return authStore.user.displayName;
  if (authStore.user?.email) return authStore.user.email.split("@")[0];
  return t("common.fields.user");
});

const userEmail = computed(() => authStore.user?.email ?? "");

const userInitial = computed(() => {
  const name = userName.value;
  return name.charAt(0).toUpperCase();
});

const userMenuItems = computed(() => [
  {
    label: userEmail.value,
    icon: "pi pi-user",
    command: () => {
      router.push({
        name: "userMe",
      });
    },
  },
  {
    label: t("common.actions.sign_out"),
    icon: "pi pi-sign-out",
    command: handleLogout,
  },
]);

const toggleMenu = (event: Event) => {
  menu.value.toggle(event);
};

async function handleLogout() {
  await authStore.logout();
}
</script>
